<template>
  <div class="box-outline mt-4">
    <div class="mx-2 my-3">
      <h5 class="mb-3">Xaccel Stream Configuration</h5>
      <div v-if="isInitialMode">
        <p class="">Stream has no Metadata, so load options to preview, and then select a stream from available encoders to populate the data.</p>
        <!-- Encoder -->
        <div class="mb-3">
          <label><strong>Encoder</strong></label>
          <select v-model="encoderValue" class="form-select">
            <option value="" disabled>Select encoder...</option>
            <option v-for="p in encoderOptions" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>

        <!-- Streams dropdown (after encoder selected) -->
        <div class="mb-3" v-if="encoderValue">
          <label><strong>Stream</strong></label>

          <select v-model="initStreamId" class="form-select" :disabled="initLoading" @change="selectInitStream">
            <option value="" disabled>
              {{ initLoading ? 'Loading streams...' : 'Select stream...' }}
            </option>
            <option v-for="s in initStreamOptions" :key="s.id" :value="s.id">
              {{ s.name }} ({{ s.id }})
            </option>
          </select>
        </div>

        <div v-if="error" class="error-message mb-2">
          {{ error }}
        </div>
        <div v-if="initialEncoderData" class="channel-details-card p-3 m-3">
          <div class="fw-bold mb-2">
            This is the data you loaded, click save to confirm the selection
          </div>

          <div class="row g-2 small">
            <div class="col-12 col-md-6">
              <strong>Encoder:</strong> {{ initialEncoderData.encoder || '—' }}
            </div>
            <div class="col-12">
              <strong>Stream ID:</strong> {{ initialEncoderData.id || '—' }}
            </div>
            <div class="col-12">
              <strong>Stream Name:</strong> {{ initialEncoderData.name || '—' }}
            </div>

            <div class="col-12 mt-2" v-if="Array.isArray(initialEncoderData.inputs) && initialEncoderData.inputs.length">
              <strong>Inputs:</strong>
              <ul class="mb-0 mt-1 ps-3">
                <li v-for="(u, i) in initialEncoderData.inputs" :key="i" class="text-break">
                  {{ u }}
                </li>
              </ul>
            </div>

            <div class="col-12 mt-2" v-if="initialEncoderData.output">
              <strong>Output:</strong>
              <div class="font-monospace text-break mt-1">{{ initialEncoderData.output }}</div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-2">
          <a role="button" class="procricket-btn" @click="confirmInitStream" :class="{ disabled: !canConfirmInit }">
            Confirm Encoder Data
          </a>
          <a role="button" class="procricket-btn secondary" @click="close">Close</a>
        </div>
      </div>

      <div v-else>
      <div class="mb-3">
        <label><strong>Encoder</strong></label>
        <input type="text" class="form-control" :value="encoderValue" disabled />
        <div class="small text-muted mt-2">
          <div>
            This encoder data was imported from the selected encoder stream. To change it select one of the following options.
          </div>
          <div class="mt-2 d-flex">
            <a role="button" class="procricket-btn btn-xs" @click="removeMetadata" >
              Remove stream data for this channel
            </a>
            <a role="button" class="procricket-btn btn-xs ms-3" @click="previewDifferentStreams" >
              Preview different streams for this channel
            </a>
          </div>

        </div>
        <div class="mt-3">
          <label><strong>Stream Origin</strong></label>
          <select v-model="originValue" class="form-select">
            <option value="" disabled>Select origin...</option>
            <option v-for="o in originOptions" :key="o" :value="o">{{ o }}</option>
          </select>
        </div>

        <div class="mt-2">
          <strong>Stream ID: #</strong>
          <strong><span class="value-lg">{{ streamId }}</span></strong>
        </div>
      </div>

        <div class="mb-3">
          <label for="streamName"><strong>Stream Name</strong></label>
          <input id="streamName" v-model="streamName" type="text" class="form-control" placeholder="PC | SkySports Something" disabled/>
        </div>

      <!-- Name -->
      <div class="mb-3">
        <label for="channelName"><strong>Channel Name</strong></label>
        <input id="channelName" v-model="channelName" type="text" class="form-control" placeholder="PC | SkySports Something" disabled/>
      </div>

      <!-- INPUT URLs -->
        <div class="mb-3">
          <label><strong>Input URLs</strong></label>
          <Draggable
              v-model="inputUrlFields"
              item-key="id"
              handle=".drag-handle"
              :animation="150"
              class="d-flex flex-column gap-2"
          >
            <template #item="{ element, index }">
              <div class="d-flex align-items-center gap-2">
                <!-- drag handle -->
                <span class="drag-handle" title="Drag to reorder" style="cursor: grab; user-select: none;">
                  ⋮⋮
                </span>
                <input v-model="element.url" type="text" class="form-control" />
                <button class="procricket-btn btn-xs procricket-btn-dark" @click="removeInput(index)" >
                  −
                </button>
              </div>
            </template>
          </Draggable>
          <br />
          <button class="procricket-btn btn-xs" @click.prevent="openAddUrlModal">Add Input Stream</button>
        </div>

        <!-- Add URL Modal -->
        <div
            v-if="showAddUrlModal"
            class="modal d-block"
            tabindex="-1"
            role="dialog"
            @click.self="closeAddUrlModal"
        >
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">

              <div class="modal-header">
                <div class="d-flex flex-column">
                  <h4 class="modal-title fw-bold mb-1">Add Input URL</h4>
                  <div class="medium text-muted">Select one of the options below</div>
                </div>
                <button role="button" class="close close-modal" aria-label="Close" @click="closeAddUrlModal" >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div class="modal-body">
                <!-- Mode Tabs -->
                <div class="add-url-tabs mb-3">
                  <button type="button" class="add-url-tab" :class="{ active: addUrlMode === 'm3u' }" @click="addUrlMode = 'm3u'" >
                    Select from M3U
                  </button>

                  <button type="button" class="add-url-tab" :class="{ active: addUrlMode === 'manual' }" @click="addUrlMode = 'manual'" >
                    Insert hardcoded
                  </button>
                </div>

                <!-- MANUAL MODE -->
                <div v-if="addUrlMode === 'manual'">
                  <div class="mb-2">
                    <label class="form-label"><strong>Direct URL Insert</strong></label>
                    <input v-model="addUrlValue" type="text" class="form-control"
                        placeholder="https://example.com/stream.m3u8" @keyup.enter="confirmAddUrl" />
                    <div class="small text-muted mt-1">
                      Paste a full URL and press Enter or click Add.
                    </div>
                  </div>
                </div>

                <!-- M3U MODE -->
                <div v-else>
                  <div class="mb-3">
                    <label class="form-label"><strong>IPTV Option</strong></label>
                    <select v-model="addUrlPreset" class="form-select">
                      <option value="" disabled>Select IPTV option...</option>
                      <option v-for="p in iptvLinks" :key="p" :value="p">{{ p }}</option>
                    </select>
                    <div class="small text-muted mt-1">
                      Choose a IPTV from which to load streams, then select a stream.
                    </div>
                  </div>

                  <div class="mb-2" v-if="addUrlPreset">
                    <label class="form-label"><strong>Stream</strong></label>

                    <Multiselect
                        v-model="selectedIptvUrl"
                        placeholder="Search stream name..."
                        :searchable="true"
                        :options="displayedIptvStreams"
                        label="name"
                        valueProp="url"
                        track-by="url"
                        :appendToBody="true"
                        :filterResults="false"
                        :disabled="iptvLoading || !iptvStreamOptions.length"
                        class="multiselect-blue"
                        @search-change="updateIptvSearchQuery"
                    />

                    <div v-if="iptvLoading" class="small text-muted mt-1">Loading streams…</div>
                    <div v-else-if="!iptvStreamOptions.length" class="small text-muted mt-1">No streams returned</div>
                    <div v-else class="small mt-1">
                      After selecting press the Add button.
                    </div>
                  </div>
                </div>

                <div v-if="addUrlError" class="error-message mt-2">{{ addUrlError }}</div>
              </div>

              <div class="modal-footer">
                <a role="button" class="btn cancel-btn" @click="closeAddUrlModal">Cancel</a>
                <a role="button" class="btn signout-popup text-white" @click="confirmAddUrl" >
                  Add
                </a>
              </div>

            </div>
          </div>
        </div>


        <!-- Backdrop (darken around modal) -->
      <div v-if="showAddUrlModal" class="modal-backdrop fade show"></div>
      <div v-if="error" class="error-message mb-2">
        {{ error }}
      </div>

      <div v-if="hasUnsavedChanges" class="text-danger small mb-2">
        Changes have been made to Input URLs. Please save to apply updates on Encoder site.
      </div>
      <div v-if="showOutputPreview" class="mt-3 p-2 rounded border small">
        <div class="fw-bold mb-2 channel-mismatch-text">Be aware: Output URL on Encoder will change after Save Stream is clicked</div>

        <div class="mb-2">
          <div class="text-muted">Current</div>
          <div class="font-monospace text-truncate">{{ currentOutputUrl }}</div>
        </div>

        <div>
          <div class="text-muted">New</div>
          <div class="font-monospace text-truncate">{{ newOutputUrl }}</div>
        </div>
      </div>
      <div class="d-flex gap-2 mt-4">
        <a role="button" class="procricket-btn" @click="saveStream">Save Stream</a>
        <a role="button" class="procricket-btn secondary" @click="close">Close</a>
      </div>

    </div>
    </div>
  </div>
</template>


<script>
import { defineComponent } from "vue"
import Multiselect from "@vueform/multiselect"
import "@vueform/multiselect/themes/default.css"
import { buildOutputURL } from "@/utils/bapi-functions";
import Draggable from "vuedraggable"


export default defineComponent({
  components: {
    Multiselect, Draggable,
  },
  name: "ChannelXaccelStreams",

  props: {
    row: { type: Object, required: true },
    initStreamOptions: { type: Array, default: () => [] },
    initLoading: { type: Boolean, default: false },
    initialEncoderData: { type: Object, required: true },

    iptvStreams: { type: Array, default: () => [] },
    iptvLoading: { type: Boolean, default: false },

    hardcodedData: {
      type: Object,
      default: () => ({ encoders: [], origins: [], iptvLinks: [] })
    },
  },


  data() {
    return {
      encoderValue: "",
      originValue: "",
      inputUrlFields: [],
      initStreamId: "",
      error: "",
      showAddUrlModal: false,
      addUrlValue: "",
      iptvSearchQuery: "",
      selectedIptvUrl: "",
      hasUnsavedChanges: false,
      isSyncingFromRow: false,
      addUrlMode: "manual",
      isInitialMode: false,

      addUrlPreset: "",
      addUrlError: "",
    }
  },

  computed: {
    encoderOptions() {
      return Array.isArray(this.hardcodedData?.encoders) ? this.hardcodedData.encoders : []
    },
    originOptions() {
      return Array.isArray(this.hardcodedData?.origins) ? this.hardcodedData.origins : []
    },
    iptvLinks() {
      return Array.isArray(this.hardcodedData?.iptvLinks) ? this.hardcodedData.iptvLinks : []
    },
    streamId() { return this.row?.streamId ?? null },
    streamName() { return this.row?.streamName ?? null },
    channelName() { return this.row?.channelName ?? "" },
    // channels.stream_app, sent by the admin API as appName
    streamApp() { return (this.row?.rawChannel?.appName ?? "").trim() },
    showOutputPreview() {
      const cur = (this.currentOutputUrl || "").trim();
      const next = (this.newOutputUrl || "").trim();
      return !!cur && !!next && cur !== next;
    },
    canConfirmInit() { return !!this.encoderValue && !!this.initStreamId && !this.initLoading },
    iptvStreamOptions() {
      return (this.iptvStreams || [])
          .map(s => ({
            name: s.name ?? s.title ?? s.channelName ?? "",
            url: s.url ?? s.streamUrl ?? s.link ?? ""
          }))
          .filter(x => x.url);
    },
    displayedIptvStreams() {
      const q = (this.iptvSearchQuery || "").trim().toLowerCase();
      if (!q) return this.iptvStreamOptions;

      // IMPORTANT: search ONLY by name
      return this.iptvStreamOptions.filter(s =>
          (s.name || "").toLowerCase().includes(q)
      );
    },
    newOutputUrl() {
      // same params you use in handleSaveStream
      return buildOutputURL(this.originValue, this.streamApp, this.channelName, this.row.rawChannel.eventId);
    },
    currentOutputUrl() {
      // your row already has outputUrl in buildEventChannelRows()
      return (this.row?.outputUrl || "").trim();
    },
  },

  watch: {
    // If inputUrlFields is an array of objects (as you have), you need deep here
    inputUrlFields: {
      deep: true,
      handler() {
        if (this.isSyncingFromRow) return;
        this.hasUnsavedChanges = true;
      }
    },
    addUrlPreset(val) {
      this.selectedIptvUrl = "";
      this.iptvSearchQuery = "";
      if (!val) return;
      this.$emit("iptv-load", { providerName: val });
    },
    // Simplify the row watcher
    row: {
      immediate: true,
      deep: true,
      handler(r) {
        this.isSyncingFromRow = true;

        this.encoderValue = (r?.encoder || "").trim();
        this.originValue = (r?.origin || "").trim();
        this.inputUrlFields = Array.isArray(r?.inputSources)
            ? r.inputSources.filter(Boolean).map(u => ({
              id: crypto.randomUUID(),
              url: String(u).trim(),
            }))
            : [];

        this.initStreamId = "";
        this.error = "";
        this.hasUnsavedChanges = false;

        this.$nextTick(() => { this.isSyncingFromRow = false; });
      }
    },

    encoderOptions: {
      immediate: true,
      deep: true,
      handler(opts) {
        if (!this.encoderValue) return
        if (!Array.isArray(opts) || !opts.length) return
        const normalizedOpts = opts.map(o => String(o).trim())
        const normalizedValue = String(this.encoderValue).trim()

        const match = normalizedOpts.find(o => o.toLowerCase() === normalizedValue.toLowerCase())
        if (match) {
          this.encoderValue = match
        } else {
          console.error("encoding not found")
        }
      }
    },

    originOptions: {
      immediate: true,
      deep: true,
      handler(opts) {
        if (!this.originValue) return
        if (!Array.isArray(opts) || opts.length === 0) return
        if (!opts.includes(this.originValue)) {
          this.originValue = ""
        }
      }
    },

    encoderValue(val, oldVal) {
      // keep your init-mode behavior
      if (!this.isInitialMode) return;
      this.initStreamId = "";
      if (val && val !== oldVal) {
        this.$emit("init-load-stream-ids", { encoder: val });
      }
    },
  },

  methods: {
    saveStream() {
      this.error = ""
      if (!this.encoderValue) return (this.error = "Encoder is required.")
      if (!this.originValue) return (this.error = "Origin is required.")
      if (!this.streamApp) return (this.error = "This channel has no stream app set, so its output URL can't be built.")

      const inputs = this.inputUrlFields
          .map(x => String(x?.url || "").trim())
          .filter(Boolean);

      if (!inputs.length) return (this.error = "At least one input URL is required.")

      this.$emit("save-stream", {
        encoder: this.encoderValue,
        stream_identifier: this.streamId,
        origin: this.originValue,
        streamApp: this.streamApp,
        channelName: this.channelName,
        inputs,
      })
      this.hasUnsavedChanges = false;
    },
    confirmInitStream() {
      this.$emit("confirm-selection", {
        channelId: this.row.channelId,
        encoder: this.encoderValue,
        stream_identifier: this.initialEncoderData.id,
      })
    },
    selectInitStream() {
      this.error = ""
      if (!this.encoderValue) return (this.error = "Encoder is required.")
      if (!this.initStreamId) return (this.error = "Stream selection is required.")
      this.$emit("init-select-stream", {
        encoder: this.encoderValue,
        stream_identifier: Number(this.initStreamId),
      })
    },

    removeInput(i) {
      this.inputUrlFields.splice(i, 1)
    },

    close() { this.$emit("close") },
    removeMetadata() {
      this.clearInitialEncoderData()
      this.$emit("remove-channel-metadata", { channelId: this.row.rawChannel.channelId})
    },
    clearInitialEncoderData() {
      this.$emit("clear-initial-encoder-data");
    },
    previewDifferentStreams() {
      this.isInitialMode = true;
      this.$emit("init-load-stream-ids", { encoder: this.encoderValue });
    },

    openAddUrlModal() {
      this.showAddUrlModal = true;
      this.addUrlValue = ""
      this.addUrlPreset = ""
      this.addUrlError = ""
      this.selectedIptvUrl = ""
    },

    closeAddUrlModal() {
      this.showAddUrlModal = false
      this.addUrlError = ""
      this.addUrlValue = ""
      this.addUrlPreset = ""
      this.selectedIptvUrl = ""
    },
    updateIptvSearchQuery(q) {
      this.iptvSearchQuery = q || "";
    },
    confirmAddUrl() {
      this.addUrlError = "";
      const finalUrl = (this.selectedIptvUrl || this.addUrlValue || "").trim();

      if (!finalUrl) {
        this.addUrlError = "Please select a stream or enter a URL.";
        return;
      }

      this.inputUrlFields.push({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        url: finalUrl,
      });

      this.closeAddUrlModal();
    }
  },
  beforeMount() {
    if (!this.streamId){
      this.isInitialMode = true;
    }
  }
})
</script>
